import { ReactNode } from 'react'
import { useFormContext } from 'react-hook-form'
import get from 'lodash-es/get'

import { MergeElementProps } from '@/lib/core/interfaces/common.types'
import { FormFieldValidation } from '@/lib/core/interfaces/forms.types'
import { ValidationBadge } from '@/lib/core/components/ValidationBadge'

import {
  FieldError,
  FieldErrorMessage,
  Input,
  InputWrapper,
  Label,
  LeftAdorment,
  RightAdorment,
} from './TextField.styles'

export type TextFieldProps = MergeElementProps<
  'input',
  {
    displayInline?: boolean
    label?: string
    leftAdorment?: ReactNode
    name: string
    rightAdorment?: ReactNode
    validation?: FormFieldValidation
    dataTestId?: string
  }
>

export const TextField = ({
  displayInline,
  label,
  leftAdorment,
  disabled,
  name,
  rightAdorment,
  type,
  validation = FormFieldValidation.IDLE,
  dataTestId,
  ...props
}: TextFieldProps) => {
  const {
    register,
    formState: { errors },
  } = useFormContext()
  const error = get(errors, `${name}.message`)
  const isValidating = validation === FormFieldValidation.VALIDATING
  const isBadgeActive = validation !== FormFieldValidation.IDLE
  const isDisabled = disabled || isValidating
  const baseTestId = dataTestId || name

  return (
    <>
      <Label
        $required={!!label && props.required}
        $displayInline={displayInline}
        $disabled={disabled}
        htmlFor={name}
        data-testid={baseTestId ? `${baseTestId}-label` : undefined}
      >
        {label}
      </Label>
      <InputWrapper $disabled={isDisabled}>
        {leftAdorment && <LeftAdorment>{leftAdorment}</LeftAdorment>}
        <Input
          id={name}
          type={type}
          $hasError={!!error}
          disabled={isDisabled}
          $leftAdorment={!!leftAdorment}
          $rightAdorment={!!rightAdorment}
          {...register(name)}
          {...props}
          data-testid={baseTestId ? `${baseTestId}-input` : undefined}
        />
        {rightAdorment && <RightAdorment>{rightAdorment}</RightAdorment>}
        {isBadgeActive && <ValidationBadge placement="formControl" status={validation} />}
      </InputWrapper>
      <FieldError>
        {error && (
          <FieldErrorMessage
            $displayInline={displayInline}
            $isValidating={isBadgeActive}
            data-testid={baseTestId ? `${baseTestId}-error` : undefined}
          >
            {typeof error === 'string' ? error : error?.message?.toString()}
          </FieldErrorMessage>
        )}
      </FieldError>
    </>
  )
}
